import React, { useState, useRef, useEffect } from "react";

// BookingSlotButton component - matches your Next.js structure
function BookingSlotButton({ slot, hour, hasNearby, onShowNearby, onBook }) {
  const slotDate = new Date(slot.DateTime);
  const timeString = slotDate.toLocaleTimeString("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
  });

  if (slot.IsAvailable) {
    return (
      <div className="booking-slot rounded float-left bg-dark available">
        <p className="small mb-0 text-center">
          {slot.SlotType}
          <button
            onClick={() => onBook(slot)}
            className="text-white"
            style={{
              background: "none",
              border: "none",
              padding: 0,
              font: "inherit",
              cursor: "pointer",
              textDecoration: "none",
              whiteSpace: "normal",
            }}
          >
            {timeString}
            <br />
            {slot.Duration} min
            <br />£{(slot.Cost || 0).toFixed(2)}
          </button>
        </p>
      </div>
    );
  } else if (hasNearby) {
    return (
      <div className="booking-slot rounded float-left">
        <p className="small mb-0 text-mid-grey text-center">
          {timeString}
          <button
            type="button"
            className="available-nearby text-decoration-none border-0 bg-transparent m-0"
            onClick={() => onShowNearby(slot)}
            style={{
              background: "none",
              border: "none",
              padding: 0,
              font: "inherit",
              cursor: "pointer",
            }}
          >
            <p className="text-orange mt-2 mb-0 small">Available nearby</p>
          </button>
        </p>
      </div>
    );
  } else {
    return (
      <div className="booking-slot rounded float-left">
        <p className="small mb-0 text-mid-grey text-center">
          {timeString}
          <br />
          Booked
          <br />
        </p>
      </div>
    );
  }
}

// NearbyModal component - matches your Next.js structure
function NearbyModal({ open, onClose, modalSlot, nearbyOptions }) {
  if (!open || !modalSlot) return null;

  const slotDate = new Date(modalSlot.DateTime);
  const modalId = `alternative-pitches-${slotDate
    .toISOString()
    .replace(/[:.]/g, "-")}`;

  return (
    <div
      className="modal fade show"
      style={{ display: "block" }}
      tabIndex="-1"
      aria-hidden="false"
    >
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">Nearby pitches</h5>
            <button
              type="button"
              className="close"
              onClick={onClose}
              aria-label="Close"
            >
              <span aria-hidden="true">&times;</span>
            </button>
          </div>
          <div className="modal-body">
            <div className="container">
              <div className="row">
                <div className="col-12">
                  <p className="card-text">
                    {nearbyOptions.length}{" "}
                    {nearbyOptions.length >= 2 ? "pitches" : "pitch"} nearby{" "}
                    {nearbyOptions.length >= 2 ? "have" : "has"}{" "}
                    {slotDate.toLocaleTimeString("en-GB", {
                      hour: "2-digit",
                      minute: "2-digit",
                    })}{" "}
                    available to book.
                  </p>
                  {nearbyOptions.map((nearbyPitch, index) => {
                    const nearbySlotDate = new Date(nearbyPitch.date_time);
                    const bookingUrl = `${nearbyPitch.pitch_url}/${
                      nearbySlotDate.toISOString().split("T")[0]
                    }/${nearbyPitch.duration}/${nearbySlotDate
                      .getHours()
                      .toString()
                      .padStart(2, "0")}${nearbySlotDate
                      .getMinutes()
                      .toString()
                      .padStart(2, "0")}`;

                    return (
                      <div key={index} className="row justify-content-center">
                        <div className="col-12 border rounded p-3 mb-3">
                          <div className="pitch-button d-flex flex-column justify-content-center">
                            <p className="small mb-0">
                              <strong>{nearbyPitch.product_title}</strong>
                            </p>
                            <p className="small">
                              {nearbyPitch.venue_name}
                              <br />
                              {nearbyPitch.post_code}
                              <br />
                              {nearbyPitch.distance_from?.toFixed(2)} miles away
                            </p>
                            <p className="small mb-0">
                              <a
                                href={bookingUrl}
                                className="book-nearby-pitch btn btn-sm btn-outline-orange mb-2 mr-2"
                              >
                                <small>Book</small>
                              </a>
                              <a
                                href={`/venues/${nearbyPitch.venue_url}/${
                                  nearbyPitch.product_category_url
                                }/${nearbyPitch.product_id}/${
                                  nearbyPitch.product_name
                                }?from_date=${
                                  nearbySlotDate.toISOString().split("T")[0]
                                }&booking_slot_time=${nearbySlotDate.getHours()}`}
                                className="view-nearby-pitch btn btn-sm btn-link text-orange text-decoration-none mb-2 mr-2"
                              >
                                <small>
                                  <strong>View all availability</strong>
                                </small>
                              </a>
                            </p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onClose}
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Main BookingCalendar component
export default function BookingCalendar({ initialData }) {
  // Helper function to get URL parameter
  const getQueryParameter = (param) =>
    new URLSearchParams(window.location.search).get(param);

  // Check for booking_slot_time URL parameter, otherwise use default
  const urlBookingSlotTime = getQueryParameter("booking_slot_time");

  // Parse calendar_offset which might be in HHMM format (e.g., 1458 = 14:58)
  const parseTimeToHour = (timeValue) => {
    if (!timeValue) return 17; // Default to 5 PM

    const timeStr = timeValue.toString();
    if (timeStr.length <= 2) {
      // Already just an hour (e.g., 17)
      return parseInt(timeStr, 10);
    } else if (timeStr.length === 3 || timeStr.length === 4) {
      // HHMM format (e.g., 1458 or 958)
      const hour = Math.floor(timeValue / 100);
      return hour;
    }
    return 17; // Default fallback
  };

  const initialBookingSlotTime = urlBookingSlotTime
    ? parseInt(urlBookingSlotTime, 10)
    : 17; // Always default to 5 PM when no URL parameter, regardless of backend calendar_offset

  const [bookingData, setBookingData] = useState(initialData || {});
  const [bookingSlotTime, setBookingSlotTime] = useState(
    initialBookingSlotTime
  );
  const [modalSlot, setModalSlot] = useState(null);
  const [nearbyOptions, setNearbyOptions] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [scrollPosition, setScrollPosition] = useState(0);
  const [isAtLeftEnd, setIsAtLeftEnd] = useState(true); // Start as true, will be corrected after scroll
  const [isAtRightEnd, setIsAtRightEnd] = useState(false);

  // Helper function to get Monday of a given date (fixed to avoid time zone issues)
  const getMondayOfWeek = (date) => {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1); // Adjust when day is Sunday
    const monday = new Date(d.getFullYear(), d.getMonth(), diff);
    return monday;
  };

  // Helper function to determine multiplier based on booking slot count
  const getScrollMultiplier = () => {
    const firstDaySlots =
      bookingData?.booking_slots_by_day?.[0]?.booking_slots || [];
    const bookingSlotCount = firstDaySlots.length;

    // Determine scroll multiplier based on slot count
    if (bookingSlotCount === 17) return 81;
    if (bookingSlotCount === 34) return 162;
    if (bookingSlotCount === 68) return 324;
    return 81; // Default fallback
  };

  // Get initial from_date from URL or default to Monday of current week or first available data
  const getInitialFromDate = () => {
    const urlFromDate = getQueryParameter("from_date");

    if (urlFromDate) {
      const dateFromUrl = new Date(urlFromDate);
      return getMondayOfWeek(dateFromUrl);
    }

    // If no URL date, check if we have data available
    if (initialData?.booking_slots_by_day?.length > 0) {
      // Use the first available date in the data
      const firstAvailableDate = new Date(
        initialData.booking_slots_by_day[0].Date
      );
      return getMondayOfWeek(firstAvailableDate);
    }

    // Fallback to today's date
    return getMondayOfWeek(new Date());
  };

  const [currentFromDate, setCurrentFromDate] = useState(getInitialFromDate);

  // Validate and adjust currentFromDate based on available data after component mounts
  useEffect(() => {
    if (bookingData?.booking_slots_by_day?.length) {
      const { min, max } = getAvailableDateRange();

      // Always ensure we can navigate to the earliest available date (today)
      // regardless of URL parameters, since backend loads 30 days from today
      if (min) {
        const earliestAllowedMonday = getMondayOfWeek(min);
        const urlFromDate = getQueryParameter("from_date");

        if (urlFromDate) {
          const dateFromUrl = new Date(urlFromDate);
          const mondayFromUrl = getMondayOfWeek(dateFromUrl);

          // If URL date is before our earliest data, use the earliest data Monday
          // If URL date is after our latest data, use today's Monday
          if (mondayFromUrl < earliestAllowedMonday) {
            setCurrentFromDate(earliestAllowedMonday);
          } else if (max && mondayFromUrl > getMondayOfWeek(max)) {
            setCurrentFromDate(getMondayOfWeek(new Date()));
          }
          // Otherwise, keep the URL date (it's within range)
        }
      }
    }
  }, [bookingData]);

  // Handle initial scroll position to show target time
  useEffect(() => {
    const scrollContainer = document.querySelector(
      ".bookings-calendar-scroll-container"
    );
    const currentWeekData = getCurrentWeekData();

    if (scrollContainer && currentWeekData?.length > 0) {
      // Get multiplier for interval type (30min vs 60min)
      const multiplier = getScrollMultiplier();

      // Calculate scroll position using backend formula: (booking_slot_time - 7) * multiplier
      const startHour = 7; // Backend uses 7am as start reference
      const targetHour = bookingSlotTime; // 17 for 5pm by default
      const hoursFromStart = Math.max(0, targetHour - startHour);
      const targetScrollPosition = hoursFromStart * multiplier;

      // Scroll to show target time
      setTimeout(() => {
        scrollContainer.scrollLeft = targetScrollPosition;
        // Give the scroll a moment to complete before checking position
        setTimeout(() => {
          checkScrollPosition();
        }, 50);
      }, 100); // Small delay to ensure DOM is ready
    }
  }, [currentFromDate, bookingSlotTime, bookingData]); // Added bookingData dependency

  // Calculate breakpoint based on when last time slots are visible
  const findBreakpoint = () => {
    const scrollContainer = document.querySelector(
      ".bookings-calendar-scroll-container"
    );
    if (!scrollContainer) return 1000; // fallback

    // Get multiplier for interval type (30min vs 60min)
    const multiplier = getScrollMultiplier();

    // Find the latest hour in the data
    let latestHour = 0;
    bookingData?.booking_slots_by_day?.forEach((day) => {
      day.booking_slots?.forEach((slot) => {
        const slotDate = new Date(slot.DateTime);
        const hour = slotDate.getHours();
        if (hour > latestHour) {
          latestHour = hour;
        }
      });
    });

    // Calculate breakpoint using same formula as backend: (latest_hour - 7) * multiplier
    const startHour = 7; // Backend uses 7am as start reference
    const hoursFromStart = Math.max(0, latestHour - startHour);
    const pixelsToLatestHour = hoursFromStart * multiplier;

    // Stop scrolling when the latest hour becomes visible
    // Subtract container width so we stop when latest slots are visible, not when we reach them
    const breakpoint = Math.max(
      0,
      pixelsToLatestHour - scrollContainer.clientWidth + multiplier
    ); // 162px buffer

    return breakpoint;
  };

  // Check scroll position and update arrow states (matches ERB logic)
  const checkScrollPosition = () => {
    const scrollContainer = document.querySelector(
      ".bookings-calendar-scroll-container"
    );
    if (scrollContainer) {
      const currentPosition = scrollContainer.scrollLeft;
      const breakpoint = findBreakpoint();

      // Constrain scroll position - don't allow scrolling beyond breakpoint
      if (currentPosition > breakpoint + 50) {
        // 50px tolerance for smooth scrolling
        scrollContainer.scrollLeft = breakpoint + 50;
        return; // Exit early since we've corrected the position
      }

      setScrollPosition(currentPosition);
      const leftEnd = currentPosition <= 10;
      const rightEnd = currentPosition > breakpoint;
      setIsAtLeftEnd(leftEnd);
      setIsAtRightEnd(rightEnd);
    }
  };

  // Add scroll event listener
  useEffect(() => {
    const scrollContainer = document.querySelector(
      ".bookings-calendar-scroll-container"
    );
    if (scrollContainer) {
      scrollContainer.addEventListener("scroll", checkScrollPosition);
      return () =>
        scrollContainer.removeEventListener("scroll", checkScrollPosition);
    }
  }, [bookingData]);

  // Calculate to_date (6 days after from_date)
  const currentToDate = new Date(currentFromDate);
  currentToDate.setDate(currentFromDate.getDate() + 6);

  // Helper functions for ordinals
  const getOrdinal = (n) => {
    const s = ["th", "st", "nd", "rd"];
    const v = n % 100;
    return n + (s[(v - 20) % 10] || s[v] || s[0]);
  };

  // Format date range like ERB
  const formatDateRange = () => {
    const fromDay = getOrdinal(currentFromDate.getDate());
    const toDay = getOrdinal(currentToDate.getDate());
    const toMonth = currentToDate.toLocaleDateString("en-GB", {
      month: "short",
    });
    return `${fromDay} - ${toDay} ${toMonth}`;
  };

  // Get available date range from loaded data (30 days starting from today)
  const getAvailableDateRange = () => {
    if (!bookingData?.booking_slots_by_day?.length)
      return { min: null, max: null };

    const dates = bookingData.booking_slots_by_day.map(
      (day) => new Date(day.Date)
    );
    return {
      min: new Date(Math.min(...dates)),
      max: new Date(Math.max(...dates)),
    };
  };

  // Filter booking data for current week
  const getCurrentWeekData = () => {
    if (!bookingData?.booking_slots_by_day?.length) return [];

    const weekEnd = new Date(currentFromDate);
    weekEnd.setDate(currentFromDate.getDate() + 6);
    // Set to end of day to include all slots on the 7th day
    weekEnd.setHours(23, 59, 59, 999);

    const filtered = bookingData.booking_slots_by_day.filter((day) => {
      const dayDate = new Date(day.Date);
      return dayDate >= currentFromDate && dayDate <= weekEnd;
    });

    return filtered;
  };

  // Calculate navigation boundaries once (MOVED BEFORE HANDLERS)
  const { min, max } = getAvailableDateRange();
  const minMonday = min ? getMondayOfWeek(min) : null;
  const maxMonday = max ? getMondayOfWeek(max) : null;

  const previousWeekDate = new Date(currentFromDate);
  previousWeekDate.setDate(currentFromDate.getDate() - 7);

  const nextWeekDate = new Date(currentFromDate);
  nextWeekDate.setDate(currentFromDate.getDate() + 7);

  const canGoPrevious =
    minMonday && previousWeekDate.getTime() >= minMonday.getTime();
  const canGoNext = maxMonday && nextWeekDate.getTime() <= maxMonday.getTime();

  // Handle week navigation (no AJAX - navigate within 30-day loaded data)
  const handlePreviousWeek = () => {
    const newDate = new Date(currentFromDate);
    newDate.setDate(currentFromDate.getDate() - 7);

    // Check if new date is within available data range (using .getTime() for proper comparison)
    if (minMonday && newDate.getTime() >= minMonday.getTime()) {
      setCurrentFromDate(newDate);
    }
  };

  const handleNextWeek = () => {
    const newDate = new Date(currentFromDate);
    newDate.setDate(currentFromDate.getDate() + 7);

    // Check if new date is within available data range (using .getTime() for proper comparison)
    if (maxMonday && newDate.getTime() <= maxMonday.getTime()) {
      setCurrentFromDate(newDate);
    }
  };

  // Handle time scrolling (matches ERB - scroll by 81px increments)
  const handleScrollLeft = () => {
    const scrollContainer = document.querySelector(
      ".bookings-calendar-scroll-container"
    );
    if (scrollContainer) {
      const currentPosition = scrollContainer.scrollLeft;
      scrollContainer.scrollLeft = currentPosition - 81;
    }
  };

  const handleScrollRight = () => {
    const scrollContainer = document.querySelector(
      ".bookings-calendar-scroll-container"
    );
    if (scrollContainer) {
      const currentPosition = scrollContainer.scrollLeft;
      scrollContainer.scrollLeft = currentPosition + 81;
    }
  };

  // Calculate the latest available time slot
  const getLatestAvailableHour = () => {
    let latestHour = 0;
    bookingData?.booking_slots_by_day?.forEach((day) => {
      day.booking_slots?.forEach((slot) => {
        const slotDate = new Date(slot.DateTime);
        const hour = slotDate.getHours();
        if (hour > latestHour) {
          latestHour = hour;
        }
      });
    });
    return latestHour;
  };

  // Check if we can scroll right (if there are slots beyond current visible time)
  const canScrollRight = () => {
    const latestHour = getLatestAvailableHour();
    // Can scroll right if there are slots beyond what we're currently showing
    // We typically show about 8-10 hours at once, so check if there are slots beyond current view
    return latestHour > bookingSlotTime + 6; // Rough estimate of visible hours
  };

  // Get current week data for display
  const currentWeekData = getCurrentWeekData();

  // Calculate the booking slot count for the draggable width (use current week data)
  const bookingSlotCount = currentWeekData.reduce((total, day) => {
    return total + (day.booking_slots?.length || 0);
  }, 0);

  // Handle booking slot click
  const handleBookSlot = (slot) => {
    if (slot.IsAvailable) {
      const slotDate = new Date(slot.DateTime);
      const dateStr = slotDate.toISOString().split("T")[0];
      const duration = slot.Duration;
      const timeStr =
        slotDate.getHours().toString().padStart(2, "0") +
        slotDate.getMinutes().toString().padStart(2, "0");
      const url = `${slot.PitchUrl}/${dateStr}/${duration}/${timeStr}`;
      window.location.href = url;
    }
  };

  // Handle "Available nearby" click
  const handleShowNearby = (slot) => {
    // Find nearby pitches for this slot
    const nearbyPitches =
      bookingData?.all_nearby_availability?.filter((nearbySlot) => {
        const nearbyDateTime = new Date(nearbySlot.date_time);
        const slotDateTime = new Date(slot.DateTime);
        return nearbyDateTime.getTime() === slotDateTime.getTime();
      }) || [];

    setModalSlot(slot);
    setNearbyOptions(nearbyPitches);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setModalSlot(null);
    setNearbyOptions([]);
  };

  // Check if a slot has nearby availability
  const hasNearbyAvailability = (slot) => {
    if (!bookingData?.all_nearby_availability) return false;

    return bookingData.all_nearby_availability.some((nearbySlot) => {
      const nearbyDateTime = new Date(nearbySlot.date_time);
      const slotDateTime = new Date(slot.DateTime);
      return nearbyDateTime.getTime() === slotDateTime.getTime();
    });
  };

  return (
    <>
      {/* Title section - exact copy of ERB structure */}
      <div className="container bookings-calendar-title">
        <div className="row">
          <div className="bookings-calendar-column-width-arrow-left"></div>
          <div className="bookings-calendar-column-width-weekdays"></div>
          <div className="bookings-calendar-column-width-content d-flex flex-wrap justify-content-center mb-2">
            <div
              className="btn btn-group btn-text mr-1 bookings-calendar-date mb-1 p-0"
              role="group"
            >
              <button
                onClick={handlePreviousWeek}
                disabled={!canGoPrevious}
                className={`btn ${
                  !canGoPrevious ? "text-light-grey" : "btn-text"
                } mr-1`}
              >
                <i className="fa fa-chevron-left icon-arrow-left-1"></i>
              </button>
              <button
                className="btn btn-text bookings-calendar-picker px-0"
                type="button"
              >
                <span className="d-none d-md-inline-block">
                  {formatDateRange()}
                </span>
                <span className="d-md-none small">{formatDateRange()}</span>
              </button>
              <button
                onClick={handleNextWeek}
                disabled={!canGoNext}
                className={`btn ${
                  !canGoNext ? "text-light-grey" : "btn-text"
                } mr-1`}
              >
                <i className="fa fa-chevron-right icon-arrow-right-1"></i>
              </button>
            </div>
          </div>
          <div className="bookings-calendar-column-width-arrow-right"></div>
        </div>
      </div>

      {/* Content section - exact copy of ERB structure */}
      <div className="bookings-calendar-content">
        <div className="row">
          {/* Left arrow */}
          <div className="bookings-calendar-nav bookings-calendar-column-width-arrow-left d-flex flex-column justify-content-center align-items-end cursor pointer">
            <button
              type="button"
              id="bookings-calendar-btn-left"
              className={`btn btn-text bookings-calendar-btn bookings-calendar-btn-left pr-1 ${
                isAtLeftEnd ? "text-light-grey" : ""
              }`}
              onClick={handleScrollLeft}
              disabled={isAtLeftEnd}
            >
              <i className="fa fa-chevron-left icon-arrow-left-1"></i>
            </button>
          </div>

          {/* Weekdays column */}
          <div className="bookings-calendar-column-width-weekdays">
            {currentWeekData?.map((bookingSlot, index) => (
              <div key={index} className="bookings-calendar-weekday small">
                <div
                  dangerouslySetInnerHTML={{
                    __html:
                      new Date(bookingSlot.Date).toLocaleDateString("en-GB", {
                        weekday: "short",
                      }) +
                      "<br>" +
                      getOrdinal(new Date(bookingSlot.Date).getDate()),
                  }}
                />
              </div>
            ))}
          </div>

          {/* Main content area */}
          <div className="bookings-calendar-column-width-content">
            <div
              id="scrollableDiv"
              className="bookings-calendar-scroll-container"
            >
              <div
                className="bookings-calendar-scroll bookings-calendar-scroll-calendar-content d-flex justify-content-start draggablecalendar"
                style={{ width: `${bookingSlotCount * 81}px` }}
              >
                <div className="bookings-calendar-scroll-calendar-content-wrapper">
                  {currentWeekData?.length > 0 ? (
                    currentWeekData.map((bookingSlot, dayIndex) => (
                      <React.Fragment key={dayIndex}>
                        {bookingSlot.booking_slots?.map((slot, slotIndex) => {
                          const hasNearby = hasNearbyAvailability(slot);
                          const slotDate = new Date(slot.DateTime);
                          const hour = slotDate.getHours();

                          return (
                            <BookingSlotButton
                              key={slotIndex}
                              slot={slot}
                              hour={hour}
                              hasNearby={hasNearby}
                              onShowNearby={handleShowNearby}
                              onBook={handleBookSlot}
                            />
                          );
                        })}
                        <div className="clearfix"></div>
                      </React.Fragment>
                    ))
                  ) : (
                    <div className="booking-slot rounded float-left d-flex flex-column justify-content-center w-100 p-5">
                      <p className="mb-0">
                        No availability during these dates yet. Please check
                        back soon.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Right arrow */}
          <div className="bookings-calendar-nav bookings-calendar-column-width-arrow-right d-flex flex-column justify-content-center align-items-center cursor pointer">
            <button
              type="button"
              id="bookings-calendar-btn-right"
              className={`btn btn-text bookings-calendar-btn bookings-calendar-btn-right pl-1 ${
                isAtRightEnd ? "text-light-grey" : ""
              }`}
              onClick={handleScrollRight}
              disabled={isAtRightEnd}
            >
              <i className="fa fa-chevron-right icon-arrow-right-1"></i>
            </button>
          </div>
        </div>
      </div>

      {/* Nearby Modal */}
      <NearbyModal
        open={showModal}
        onClose={handleCloseModal}
        modalSlot={modalSlot}
        nearbyOptions={nearbyOptions}
      />
    </>
  );
}
